import React, { useState, useEffect } from 'react';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { callLogsAPI } from '../services/api';

const CallLogs = () => {
  const [callLogs, setCallLogs] = useState([]);
  const [stats, setStats] = useState({ total_calls: 0, answered: 0, missed: 0, inbound: 0, outbound: 0, internal: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [currentPage, typeFilter, statusFilter, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const [logsRes, statsRes] = await Promise.all([
        callLogsAPI.list(params),
        callLogsAPI.getStats()
      ]);
      
      setCallLogs(logsRes.data.data || []);
      setTotalPages(logsRes.data.pages || 1);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching call logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCallIcon = (type, status) => {
    if (status === 'missed' || status === 'no-answer') {
      return <PhoneMissed className="w-4 h-4 text-red-400" />;
    }
    if (type === 'inbound') {
      return <PhoneIncoming className="w-4 h-4 text-green-400" />;
    }
    if (type === 'outbound') {
      return <PhoneOutgoing className="w-4 h-4 text-blue-400" />;
    }
    return <Phone className="w-4 h-4 text-purple-400" />;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'answered': 'bg-green-500/20 text-green-400 border-green-500/30',
      'missed': 'bg-red-500/20 text-red-400 border-red-500/30',
      'busy': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'no-answer': 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return <Badge className={colors[status] || 'bg-gray-500/20 text-gray-400'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const colors = {
      'inbound': 'bg-green-500/20 text-green-400',
      'outbound': 'bg-blue-500/20 text-blue-400',
      'internal': 'bg-purple-500/20 text-purple-400'
    };
    return <Badge variant="outline" className={`border-0 ${colors[type] || ''}`}>{type}</Badge>;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading && callLogs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Call Logs</h1>
          <p className="text-gray-400 mt-1">View and search call history</p>
        </div>
        <Button variant="outline" className="border-[#2a2a3d] text-gray-400 hover:text-white hover:bg-[#1e1e2d]">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_calls}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <PhoneIncoming className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-green-400">{stats.inbound}</p>
                <p className="text-xs text-gray-500">Inbound</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <PhoneOutgoing className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-blue-400">{stats.outbound}</p>
                <p className="text-xs text-gray-500">Outbound</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-purple-400">{stats.internal}</p>
                <p className="text-xs text-gray-500">Internal</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.answered}</p>
                <p className="text-xs text-gray-500">Answered</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <PhoneMissed className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-2xl font-bold text-red-400">{stats.missed}</p>
                <p className="text-xs text-gray-500">Missed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search calls..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px] bg-[#1e1e2d] border-[#2a2a3d] text-white">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-[#1e1e2d] border-[#2a2a3d] text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="busy">Busy</SelectItem>
                <SelectItem value="no-answer">No Answer</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Call Logs Table */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1e1e2d]">
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Type</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">From</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">To</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Duration</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Trunk</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Time</th>
                </tr>
              </thead>
              <tbody>
                {callLogs.map((log) => (
                  <tr
                    key={log.id}
                    className="border-b border-[#1e1e2d] hover:bg-[#1e1e2d]/50 transition-colors"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {getCallIcon(log.type, log.status)}
                        {getTypeBadge(log.type)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-white">{log.from_name}</p>
                        <p className="text-sm text-gray-500">{log.from_number}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-white">{log.to_name}</p>
                        <p className="text-sm text-gray-500">{log.to_number}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-white font-mono">{log.duration}</span>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(log.status)}
                    </td>
                    <td className="p-4">
                      <span className="text-gray-400">{log.trunk_name}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-gray-400 text-sm">{formatTimestamp(log.timestamp)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-[#1e1e2d]">
            <p className="text-sm text-gray-400">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="border-[#2a2a3d] text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-white px-3">{currentPage} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="border-[#2a2a3d] text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {callLogs.length === 0 && (
            <div className="p-12 text-center">
              <Phone className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No call logs found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CallLogs;
